import { Select, Space, Tooltip } from 'antd'
import { LocalityFilter } from 'components/Filters/LocalityFilter/LocalityFilter'
import { localityQuery } from 'location/locality'
import { useLocality } from 'location/LocalityProvider'
import { useRouter } from 'next/router'
import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ArrowIcon } from '../../../../assets/img/logo/Arrow'
import { CloseIcon } from '../../../../assets/img/logo/CloseIcon'
import { filtersAPI } from '../../../../axios/axios'
import { ContentPanel } from '../../../../components/atoms/ContentPanel/ContentPanel'
import type { FilterTypeENUM } from '../../../../components/Filters/0_FilteraCollection/Filters.model'
import { FiltersMainMenu } from '../../../../home/HomePageContent/MainMenu/components/Filters/FiltersMainMenu'
import { SLink } from '../../../../MainContainer/styles/Navbar.styled'
import { pathENUM } from '../../../../registrators/registrators'
import { objectsNameENUM } from '../../Map.model'
import { FilterWrapper } from '../../style/FilterWrapper.styled'
import {
  LocalityMapPanel,
  MenuWrapp,
  MenuWrappText,
  SButton,
  SButtonGroup,
  SContentPanel,
  TextWrapp,
} from '../../style/Map.styled'
import { ContentPanelContainer } from '../ContentPanelContainer/ContentPanelContainer'

interface IBarContentContainerProps {
  visiblePanel: boolean
  handleSetVisiblePanel: (visible: boolean) => void
  dataForPanel: any
  selectedType?: objectsNameENUM
  activeFilterMenu:
    | FilterTypeENUM.eventsFilters
    | FilterTypeENUM.programsFilters
  afterClearFilterCallback: () => void
  setTypeObject: (type: objectsNameENUM) => void
}

interface ISearchResult {
  name: string
  obj_url: string
}

const searchModels: Record<objectsNameENUM, number> = {
  [objectsNameENUM.programs]: 1,
  [objectsNameENUM.events]: 2,
  [objectsNameENUM.organizations]: 3,
}

const searchPaths: Record<objectsNameENUM, string> = {
  [objectsNameENUM.programs]: '/programs/',
  [objectsNameENUM.events]: '/events/',
  [objectsNameENUM.organizations]: '/units/',
}

export const BarContentContainer: FC<IBarContentContainerProps> = ({
  visiblePanel,
  handleSetVisiblePanel,
  dataForPanel,
  selectedType,
  activeFilterMenu,
  afterClearFilterCallback,
  setTypeObject,
}) => {
  const router = useRouter()
  const { locality } = useLocality()
  const abortController = useRef<AbortController | null>(null)
  const [optionsData, setOptions] = useState<
    { label: string; value: string }[]
  >([])
  const getOptions = useCallback(
    async (searchString: string) => {
      abortController.current?.abort()
      if (!selectedType || !searchString.trim()) {
        setOptions([])
        return
      }

      const controller = new AbortController()
      abortController.current = controller
      try {
        const response = await filtersAPI.mainFilter(searchString.trim(), {
          signal: controller.signal,
          params: {
            obj_model: searchModels[selectedType],
            ...localityQuery(locality),
          },
          timeout: 15000,
        })
        if (controller.signal.aborted) return
        const prefix = searchPaths[selectedType]
        const options = (response.data.result as ISearchResult[])
          .filter((result) => result.obj_url.startsWith(prefix))
          .map((result) => ({
            label: result.name,
            value: result.obj_url,
          }))
        setOptions(options)
      } catch (error) {
        if (!controller.signal.aborted) {
          setOptions([])
          console.error(error)
        }
      }
    },
    [selectedType, locality],
  )

  useEffect(() => {
    abortController.current?.abort()
    setOptions([])
    return () => abortController.current?.abort()
  }, [selectedType, locality])

  const handleOnChange = useCallback(
    (url: string) => {
      if (
        selectedType &&
        new RegExp(`^${searchPaths[selectedType]}\\d+/?$`).test(url)
      ) {
        router.push(url)
      }
    },
    [router, selectedType],
  )

  const barContent = useMemo(() => {
    if (visiblePanel) {
      return (
        <SContentPanel>
          <ContentPanel
            header={<CloseIcon onClick={() => handleSetVisiblePanel(false)} />}
            content={
              <ContentPanelContainer
                dataForPanel={dataForPanel}
                selectedType={selectedType}
              />
            }
          />
        </SContentPanel>
      )
    }
    return (
      <>
        <MenuWrapp>
          <SLink href="/">
            <MenuWrappText>
              <ArrowIcon />
            </MenuWrappText>
          </SLink>
          <TextWrapp>Что вас интересует?</TextWrapp>
        </MenuWrapp>

        <LocalityMapPanel>
          <LocalityFilter compact />
        </LocalityMapPanel>
        <Space.Compact block>
          <Select
            allowClear
            showSearch
            filterOption={false}
            placeholder="Поиск..."
            defaultActiveFirstOption={false}
            onSearch={getOptions}
            notFoundContent={null}
            onChange={handleOnChange}
            options={optionsData}
            disabled={!selectedType}
            style={{ width: '100%' }}
          />
          <Tooltip
            title={
              router.query.type ? '' : 'Для поиска выберите один из фильтров'
            }
            placement="right">
            <FilterWrapper>
              <FiltersMainMenu
                disabled={!router.query.type}
                eventsButtonDisabled={selectedType !== objectsNameENUM.events}
                programButtonDisabled={
                  selectedType !== objectsNameENUM.programs
                }
                pathToEventsFilter={pathENUM.map}
                pathToProgramsFilter={pathENUM.map}
                initialQuery={{ type: selectedType }}
                initialActiveMenuButton={activeFilterMenu}
                afterClearFilterCallback={afterClearFilterCallback}
              />
            </FilterWrapper>
          </Tooltip>
        </Space.Compact>

        <SButtonGroup>
          <SButton
            size="small"
            active={selectedType === objectsNameENUM.programs}
            onClick={() => setTypeObject(objectsNameENUM.programs)}>
            Программы
          </SButton>
          <SButton
            size="small"
            active={selectedType === objectsNameENUM.events}
            onClick={() => setTypeObject(objectsNameENUM.events)}>
            Мероприятия
          </SButton>
          <SButton
            size="small"
            active={selectedType === objectsNameENUM.organizations}
            onClick={() => setTypeObject(objectsNameENUM.organizations)}>
            Организации
          </SButton>
        </SButtonGroup>
      </>
    )
  }, [
    activeFilterMenu,
    afterClearFilterCallback,
    dataForPanel,
    getOptions,
    handleOnChange,
    handleSetVisiblePanel,
    optionsData,
    router.query.type,
    selectedType,
    setTypeObject,
    visiblePanel,
  ])

  return <>{barContent}</>
}
